/**
 * OperatorLayout (T-048).
 *
 * The app shell for every /eric/* route. Desktop-only (>= 1024px) per DES-11.
 *
 * Final design pass (Phase 3): the old TECH-19 plain-shadcn carve-out is
 * lifted (Eric's decision). The shell now matches the Eric Dashboard prototype
 * (design-final/Eric Dashboard.html): a deep-navy `Sidebar` with the light
 * logo, an "OPERATOR / ERIC" overline, the date + in-flight summary line, nav
 * items with the rose active treatment, and a "Studio v0.1 / internal" footer.
 * Consumes the Bold + Rose sidebar tokens from @studio/design-tokens.
 *
 * Source: app/docs/aldente/ui-structure.md §Layouts > "Operator dashboard
 * (Eric only)" and §Navigation > "Operator sidebar".
 *
 * Nav items are all 13 operator routes from ui-structure.md. Some routes are
 * not yet built (e.g., /eric/agent-telemetry is Phase O); the layout still
 * lists them so the structure is visible. Routes that don't exist yet
 * resolve to the operator notFound fallback, which is acceptable for a
 * surface only Eric sees during V1 build-out.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  Boxes,
  CalendarClock,
  Gauge,
  LayoutDashboard,
  type LucideIcon,
  Mail,
  MessageSquare,
  MessageSquarePlus,
  ScrollText,
  Users,
  UserCheck,
} from 'lucide-react';
import { countOperatorUnread } from '@/lib/operator';

export interface OperatorLayoutProps {
  children: ReactNode;
  userId: string;
  email: string | null;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
}

const NAV: NavSection[] = [
  {
    label: 'Run today',
    items: [
      { label: 'Today', to: '/eric', icon: LayoutDashboard },
      { label: 'Active tasks', to: '/eric/active', icon: Activity },
      { label: 'Awaiting action', to: '/eric/awaiting', icon: AlertTriangle },
      {
        label: 'Input requests',
        to: '/eric/input-requests',
        icon: MessageSquarePlus,
      },
      { label: 'Notifications', to: '/eric/notifications', icon: Bell },
      { label: 'Content queue', to: '/eric/content', icon: ScrollText },
    ],
  },
  {
    label: 'Pipeline',
    items: [
      { label: 'Clients', to: '/eric/clients', icon: Users },
      { label: 'Waitlist', to: '/eric/waitlist', icon: CalendarClock },
      { label: 'Contact inbox', to: '/eric/contact', icon: Mail },
      { label: 'Onboarding', to: '/eric/onboarding', icon: UserCheck },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Capacity', to: '/eric/capacity', icon: Gauge },
      { label: 'Reports', to: '/eric/reports', icon: BarChart3 },
      { label: 'Agent telemetry', to: '/eric/agent-telemetry', icon: MessageSquare },
      { label: 'OS', to: '/eric/os', icon: Boxes },
    ],
  },
];

export function OperatorLayout({ children, userId, email }: OperatorLayoutProps) {
  return (
    <div className="flex min-h-screen bg-[color:var(--color-white)] text-[color:var(--color-black)]">
      <OperatorSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <OperatorTopBar userId={userId} email={email} />
        <main className="flex-1 px-6 py-8 lg:px-10">
          <div className="mx-auto w-full max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * The date line shown under the operator identity. Honest, locale-formatted
 * "Weekday, Month D" from the live clock — no fabricated in-flight counts
 * (the prototype's "N tasks in flight" line is not backed by data at the
 * shell level, so we render the date alone rather than invent a number).
 */
function todayLabel(): string {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function OperatorSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <aside
      className="hidden w-60 shrink-0 flex-col border-r border-[color:var(--color-sidebar-border)] bg-[color:var(--color-sidebar-bg)] lg:flex"
      aria-label="Operator navigation"
    >
      <div className="border-b border-[color:var(--color-sidebar-border)] px-5 pb-3.5 pt-4">
        <Link
          to="/eric"
          className="block rounded-sm hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-rose)]"
          aria-label="Operator dashboard home"
        >
          <img
            src="/logo-linen-navy.svg"
            alt="Yes Please Studio"
            className="h-6 w-auto"
          />
          <span className="mt-2 block text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--color-sidebar-fg)]">
            Operator &middot; Eric
          </span>
        </Link>
      </div>
      <div className="border-b border-[color:var(--color-sidebar-border)] px-5 py-2.5">
        <p className="text-[11px] font-semibold text-[color:var(--color-sidebar-work-fg)]">
          {todayLabel()}
        </p>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {NAV.map((section) => (
          <div key={section.label} className="mb-4 last:mb-0">
            <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--color-sidebar-fg)] opacity-70">
              {section.label}
            </div>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active =
                  item.to === '/eric'
                    ? pathname === '/eric'
                    : pathname.startsWith(item.to);
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={[
                        'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-rose)]',
                        active
                          ? 'bg-[color:var(--color-sidebar-active-bg)] font-medium text-[color:var(--color-sidebar-active-fg)]'
                          : 'text-[color:var(--color-sidebar-fg)] hover:bg-[color:var(--color-navy-deep)] hover:text-[color:var(--color-sidebar-work-fg)]',
                      ].join(' ')}
                      aria-current={active ? 'page' : undefined}
                    >
                      <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-[color:var(--color-sidebar-border)] px-5 py-3 text-xs text-[color:var(--color-sidebar-fg)] opacity-70">
        Studio v0.1 &middot; internal
      </div>
    </aside>
  );
}

function OperatorTopBar({
  userId,
  email,
}: {
  userId: string;
  email: string | null;
}) {
  return (
    <header
      className="flex h-16 items-center justify-between gap-3 border-b border-[color:var(--color-rule)] bg-[color:var(--color-white)]/95 px-6 backdrop-blur lg:px-10"
      role="banner"
    >
      <div className="hidden text-sm text-[color:var(--color-text-mute)] lg:block">
        Operator surface &middot; {email ?? 'signed in'}
      </div>
      <div className="lg:hidden">
        <span className="text-sm font-semibold">Yes Please &middot; Operator</span>
      </div>
      <div className="flex items-center gap-3">
        <OperatorBell userId={userId} />
        <a
          href="/auth/sign-out"
          className="rounded-md border border-[color:var(--color-line-dark)] px-3 py-1.5 text-sm text-[color:var(--color-gray-700)] transition-colors hover:bg-[color:var(--color-linen)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-navy)]"
        >
          Sign out
        </a>
      </div>
    </header>
  );
}

function OperatorBell({ userId }: { userId: string }) {
  const [unread, setUnread] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    countOperatorUnread(userId)
      .then((n) => {
        if (!cancelled) setUnread(n);
      })
      .catch(() => {
        if (!cancelled) setUnread(null);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const label =
    unread && unread > 0 ? `${unread} unread notifications` : 'Notifications';
  return (
    <Link
      to="/eric/notifications"
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-[color:var(--color-gray-500)] transition-colors hover:bg-[color:var(--color-linen)] hover:text-[color:var(--color-gray-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-navy)]"
      aria-label={label}
    >
      <Bell size={18} strokeWidth={1.5} aria-hidden="true" />
      {unread && unread > 0 ? (
        <span
          className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-[color:var(--color-error)] px-1 text-[0.625rem] font-semibold leading-[1.1rem] text-[color:var(--color-white)]"
          aria-hidden="true"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * Narrow-viewport notice. Per DES-11 + the prototype-review decision, the
 * operator surface is desktop-only in V1. We render a friendly "open this on
 * a larger screen" panel so Eric on his phone gets a clear message rather
 * than a broken layout.
 */
export function OperatorTooNarrow() {
  return (
    <div className="lg:hidden">
      <div className="mx-auto mt-12 max-w-md rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-6 shadow-sm">
        <h1 className="font-display text-2xl italic text-[color:var(--color-black)]">
          Open this on a larger screen
        </h1>
        <p className="mt-2 text-sm text-[color:var(--color-text-mute)]">
          The operator dashboard is desktop-only in V1. Open it from a laptop
          to run today.
        </p>
      </div>
    </div>
  );
}
